// SupportChat — drop-in floating chat widget for the main app.
//
// Architecture (per MAIN_APP_SPEC §9):
//   - Reuses the panel's existing admin_tickets + admin_ticket_messages
//     tables. No parallel system; every client message lands directly
//     in the panel inbox.
//   - Finds or creates THE client's single open ticket on mount. We
//     keep one open thread per client to match the panel's typical
//     "one conversation per customer" workflow.
//   - Subscribes to admin_ticket_messages via Supabase Realtime so
//     admin replies appear live without polling.
//   - Posts new client messages as from_role='client'. RLS (migration
//     077) enforces that they can only insert into their own ticket
//     with that role — they cannot impersonate admin or AI.
//   - AI drafts (ai_draft=true) are filtered out by RLS so the client
//     only ever sees what the panel approved + sent.
//
// Mount once at the app root and it floats over everything. Hides
// itself if the user isn't signed in.

import React from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const STORAGE_KEY = 'sfa_support_chat_open';

export default function SupportChat({ currentUser }) {
  const [open, setOpen]       = React.useState(false);
  const [ticket, setTicket]   = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [body, setBody]       = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [error, setError]     = React.useState(null);
  const [unread, setUnread]   = React.useState(0);
  const scrollRef             = React.useRef(null);

  // Persist open state so a refresh doesn't lose the window.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try { if (window.localStorage.getItem(STORAGE_KEY) === '1') setOpen(true); } catch {}
  }, []);
  React.useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, open ? '1' : '0'); } catch {}
    if (open) setUnread(0);
  }, [open]);

  // Cockpit top-bar Help button dispatches `cockpit:open-help`. Listening
  // here means clicking Help opens this chat inline — no separate sign-in,
  // no redirect, no new tab.
  React.useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('cockpit:open-help', onOpen);
    return () => window.removeEventListener('cockpit:open-help', onOpen);
  }, []);

  // Surface the unread count on the top-bar Help button (the FAB that used
  // to carry this badge was removed to stop overlapping the ARIA launcher).
  React.useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('sfa:support-unread', { detail: open ? 0 : unread })); } catch {}
  }, [unread, open]);

  // ── 1. Find or create the client's ticket ─────────────────────────
  React.useEffect(() => {
    if (!currentUser?.id) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Prefer the most-recent OPEN ticket. If none exists, create one.
        const { data: existing, error: selErr } = await supabase
          .from('admin_tickets')
          .select('*')
          .eq('client_id', currentUser.id)
          .in('status', ['open', 'pending'])
          .order('last_message_at', { ascending: false })
          .limit(1);
        if (selErr) throw selErr;

        let row = (existing && existing[0]) || null;
        if (!row) {
          const { data: inserted, error: insErr } = await supabase
            .from('admin_tickets')
            .insert({
              client_id: currentUser.id,
              subject:         'Support chat',
              status:          'open',
            })
            .select('*')
            .single();
          if (insErr) throw insErr;
          row = inserted;
        }
        if (cancelled) return;
        setTicket(row);

        // Load the message history.
        const { data: msgs, error: mErr } = await supabase
          .from('admin_ticket_messages')
          .select('*')
          .eq('ticket_id', row.id)
          .order('created_at', { ascending: true });
        if (mErr) throw mErr;
        if (cancelled) return;
        setMessages(msgs || []);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser?.id]);

  // ── 2. Realtime subscription to new messages ──────────────────────
  React.useEffect(() => {
    if (!ticket?.id) return;
    const channel = supabase
      .channel(`support-chat-${ticket.id}`)
      .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'admin_ticket_messages', filter: `ticket_id=eq.${ticket.id}` },
          (payload) => {
            const row = payload.new;
            setMessages(prev => prev.some(m => m.id === row.id) ? prev : [...prev, row]);
            // Count an unread if the panel sent it and we're closed.
            if (row.from_role !== 'client' && !open) {
              setUnread(u => u + 1);
            }
          })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [ticket?.id, open]);

  // Auto-scroll to bottom on new messages / when opening.
  React.useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [messages.length, open]);

  // ── 3. Send a new client message ──────────────────────────────────
  const send = async () => {
    const text = body.trim();
    if (!text || !ticket?.id || sending) return;
    setSending(true);
    setError(null);
    // Optimistic append so the input clears immediately. The realtime
    // INSERT will reconcile (dedup by id) when the server echoes back.
    const tempId = `temp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      ticket_id: ticket.id,
      from_role: 'client',
      body: text,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimistic]);
    setBody('');

    try {
      const { data: inserted, error: insErr } = await supabase
        .from('admin_ticket_messages')
        .insert({
          ticket_id: ticket.id,
          from_role: 'client',
          author:    currentUser.id,   // panel's existing "who sent this" column
          body:      text,
        })
        .select('*')
        .single();
      if (insErr) throw insErr;
      // Replace optimistic with the real row.
      setMessages(prev => prev.map(m => m.id === tempId ? inserted : m));
    } catch (err) {
      setError(err.message || 'Failed to send');
      // Roll back optimistic on failure.
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setBody(text);
    } finally {
      setSending(false);
    }
  };

  // Don't render until we have a logged-in user.
  if (!currentUser?.id) return null;

  return (
    <>
      {/* No bottom-right FAB — Support opens from the top-bar Help button
          (via the `cockpit:open-help` event). The old emerald corner circle
          was a duplicate trigger that overlapped the Ask ARIA launcher; the
          bottom-right corner now holds exactly ONE floating launcher (ARIA).
          Unread is surfaced on the top-bar Help button via `sfa:support-unread`. */}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="relative bg-gradient-to-br from-slate-900 to-emerald-900 text-white px-4 py-3">
            <div className="text-emerald-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Six Figure Agent OS</div>
            <div className="text-sm font-bold">Support chat</div>
            <div className="text-[11px] text-emerald-200/80">We usually reply within a few hours.</div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close support chat"
              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={18}/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 bg-slate-50">
            {loading && (
              <div className="text-center text-xs text-slate-500 py-8 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin"/> Loading conversation…
              </div>
            )}
            {!loading && error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                {error}
              </div>
            )}
            {!loading && !error && messages.length === 0 && (
              <div className="text-center text-xs text-slate-500 py-8">
                Got a question? Send the first message and we'll get back to you.
              </div>
            )}
            <div className="space-y-2">
              {messages.map(m => <MessageBubble key={m.id} msg={m}/>)}
            </div>
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-200 p-2 bg-white"
          >
            <div className="flex items-end gap-2">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Type your message…"
                rows={2}
                disabled={loading || sending}
                className="flex-1 resize-none text-sm border border-slate-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!body.trim() || sending || loading}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-md p-2"
                aria-label="Send message"
              >
                {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
              </button>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 px-1">Press Enter to send · Shift+Enter for newline</div>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({ msg }) {
  const isClient = msg.from_role === 'client';
  const isAi     = msg.from_role === 'ai';
  const time     = fmtTime(msg.created_at);
  const tone     = isClient
    ? 'bg-emerald-600 text-white'
    : isAi
      ? 'bg-violet-100 text-violet-900 border border-violet-200'
      : 'bg-white text-slate-900 border border-slate-200';
  return (
    <div className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${tone} ${msg._optimistic ? 'opacity-60' : ''}`}>
        <div className="whitespace-pre-wrap break-words">{msg.body}</div>
        <div className={`text-[10px] mt-0.5 ${isClient ? 'text-emerald-100' : 'text-slate-500'}`}>{time}</div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
